import type { IHttpClient, RequestOptions } from './interfaces/i-http-client';
import { authRefreshToken } from '../auth_v2';
import { get } from 'svelte/store';
import { ApiError } from './api-error';
import { Method } from './enums/method';
import { isRetrying, RetryStatus } from 'src/stores/session/failed-requests';
import { NON_RETRYABLE_URLS } from './constants/non-retryable-urls';

/**
 * HttpClient class to wrap fetch API with additional functionalities.
 * @implements IHttpClient
 */
export class HttpClient implements IHttpClient {
	protected url!: string;
	protected timeout: number;
	protected defaultOptions: Partial<RequestOptions> = {
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json, text/plain, */*',
		},
		cache: 'default',
		mode: 'cors',
	};

	/**
	 * HttpClient class to wrap fetch API with additional functionalities.
	 * @implements - IHttpClient
	 * @param baseUrl - string - Address of the api to search for
	 * @param timeout - number (default 10000ms)
	 */
	constructor(baseUrl: string, timeout = 10000) {
		this.url = this.validateUrl(baseUrl);
		this.timeout = timeout;
	}

	public async get(path: string, options?: RequestOptions) {
		return this.sendRequest(Method.GET, path, undefined, options);
	}

	public async post(path: string, body?: object, options?: RequestOptions) {
		return this.sendRequest(Method.POST, path, body, options);
	}

	public async put(path: string, body?: object, options?: RequestOptions) {
		return this.sendRequest(Method.PUT, path, body, options);
	}

	public async delete(path: string, options?: RequestOptions) {
		return this.sendRequest(Method.DELETE, path, undefined, options);
	}

	/**
	 * Generalized method to handle HTTP requests
	 * @param method - string - HTTP method
	 * @param path - string - API endpoint path
	 * @param body - any
	 * @param options - RequestOptions
	 *  @returns Response
	 */
	private async sendRequest(method: Method, path: string, body?: object, options?: RequestOptions) {
		const abortController = new AbortController();
		let requestOptions = this.prepareRequestOptions(options);
		requestOptions.method = method;
		requestOptions.signal = abortController.signal;

		// Prepare request body inside a separate function
		requestOptions = this.prepareRequestBody(body, method, requestOptions);

		let requestUrl = path.startsWith('http') ? path : this.url + this.validatePath(path);

		if (options?.params) {
			requestUrl = this.appendQueryParams(requestUrl, options.params);
		}

		let request = new Request(requestUrl, requestOptions);
		let res: Response | undefined;

		try {
			res = await this.fetchWithTimeout(
				request,
				abortController,
				options?.timeout ?? this.timeout,
			);

			const data = await ApiError.handleResponse(res);
			(res as any).data = data;

			if (method === Method.GET && !requestUrl.startsWith('http://')) return data;

			return res;
		} catch (error) {
			// Skip retry logic for 401s on specific URLs defined in NON_RETRYABLE_URLS
			const isNonRetryable = NON_RETRYABLE_URLS.some((url) =>
				new URL(requestUrl).pathname.includes(url),
			);

			if (res?.status !== 401 || isNonRetryable) {
				return ApiError.handleFetchError(error, res?.status);
			}

			// Handle 401 error
			if (get(isRetrying) === RetryStatus.INVALID) {
				authRefreshToken().catch((error) => {
					console.error('[HTTP] Failed to refresh token:', error);
				});
			}

			return await this.retryRequestWhenReady(request, requestUrl, body, method, options);
		}
	}

	/**
	 * Retries a failed request when the retry mechanism indicates readiness.
	 * @param request
	 * @param requestUrl
	 * @param body
	 * @param method
	 * @param options
	 * @returns Promise<Response | any>
	 */
	private async retryRequestWhenReady(
		request: Request,
		requestUrl: string,
		body: any,
		method: Method,
		options: RequestOptions | undefined,
	): Promise<Response | any> {
		return new Promise((resolve, reject) => {
			const unsubscribe = isRetrying.subscribe(async (status) => {
				if (status === RetryStatus.SKIP) {
					console.warn('[HTTP] Skipping retry for request:', request.url);

					unsubscribe();
					reject(new Error('Skipping retry for request'));
					return;
				}

				if (status === RetryStatus.IN_PROGRESS) {
					return;
				}

				const clonedRequest = this.cloneRequest(request, body as any);
				const response = await this.fetchWithTimeout(
					clonedRequest.request,
					clonedRequest.abortController,
					options?.timeout ?? this.timeout,
				);
				let data: any = null;

				try {
					data = await ApiError.handleResponse(response);
					(response as any).data = data;

					if (method === Method.GET && !requestUrl.startsWith('http://')) {
						resolve(data);
						return;
					}

					if (response.status >= 400) {
						reject({ response: { data } });
						return;
					}

					resolve(response);
				} catch (error) {
					reject(ApiError.handleFetchError(error, response?.status, true));
					return;
				} finally {
					unsubscribe();
				}
			});
		});
	}

	/**
	 * Appends query parameters to the provided URL string.
	 * Uses URLSearchParams to encode key-value pairs correctly.
	 * @param url - string - The base URL to which query parameters will be appended.
	 * @param params - Record<string, any> - A key-value map of parameters to append to the URL.
	 * @returns string - The full URL with serialized query parameters.
	 */
	private appendQueryParams(url: string, params: Record<string, any>): string {
		const query = new URLSearchParams();

		for (const key in params) {
			const value = params[key];
			if (value !== undefined && value !== null) {
				query.append(key, String(value));
			}
		}

		const separator = url.includes('?') ? '&' : '?';
		return url + separator + query.toString();
	}

	/**
	 * Prepares the request body based on its type.
	 * Removes body for GET and DELETE requests.
	 * @param body - Request body
	 * @param method - HTTP method
	 * @param requestOptions - Request options to modify
	 */
	private prepareRequestBody(
		body: object | undefined,
		method: Method,
		requestOptions: RequestOptions,
	) {
		const clonedRequestOptions = { ...requestOptions };

		if ([Method.GET, Method.DELETE].includes(method)) {
			delete clonedRequestOptions.body; // Ensure body is removed for GET & DELETE
			return clonedRequestOptions;
		}

		if (body instanceof FormData) {
			delete clonedRequestOptions.headers?.['Content-Type'];
			clonedRequestOptions.body = body;
		} else {
			clonedRequestOptions.body = typeof body === 'object' ? JSON.stringify(body) : body;
		}

		return clonedRequestOptions;
	}

	/**
	 * Set authorization token for all requests initiated from the instance.
	 * @param token - string
	 * @returns void
	 */
	public setAuthToken(token: string) {
		(this.defaultOptions as any).headers['Authorization'] = `Bearer ${token}`;
	}

	/**
	 * Clear the authorization token from default headers.
	 */
	public clearAuthToken() {
		delete (this.defaultOptions as any).headers['Authorization'];
	}

	/**
	 * Set base url for all requests initiated from the instance.
	 * @param url - string
	 */
	public setUrl(url: string) {
		if (url.endsWith('/')) {
			this.url = url;
		}

		this.url = url + '/';
	}

	private async fetchWithTimeout(
		request: Request,
		abortController: AbortController,
		timeout,
	): Promise<Response> {
		let timer: NodeJS.Timeout;

		const result = (await Promise.race([
			fetch(request),
			new Promise(
				(_, reject) =>
					(timer = setTimeout(() => {
						reject(this.prepareTimeoutResponse());
						abortController.abort();
					}, timeout)),
			),
		])) as Response;

		if (timer) {
			clearTimeout(timer);
		}

		return result;
	}

	/**
	 * @param url - string
	 * @returns string
	 */
	private validateUrl(url: string) {
		if (!url) {
			console.trace();
		}

		if (!url?.endsWith('/')) {
			return url + '/';
		}

		return url;
	}

	/**
	 * @param path - string
	 * @returns string
	 */
	private validatePath(path: string) {
		if (path.startsWith('/')) {
			return path.slice(1);
		}

		return path;
	}

	/**
	 * Clone request object.
	 * fetch API does not allow reusing request objects.
	 * @param originalRequest - Request
	 * @param body - any
	 * @returns Request
	 */
	private cloneRequest(originalRequest: Request, body?: BodyInit) {
		const headers = { ...this.defaultOptions.headers };
		const abortController = new AbortController();
		let options = {
			method: originalRequest.method,
			cache: originalRequest.cache,
			mode: originalRequest.mode,
			signal: abortController.signal,
			body,
			headers: null,
		};

		originalRequest.headers.forEach((value, key) => {
			if (headers[key]) {
				headers[key] = value;
			}
		});
		options.headers = headers;
		options = this.prepareRetryRequestBody(
			body as object,
			originalRequest.method as Method,
			options,
		) as any;

		return {
			request: new Request(originalRequest.url, options),
			abortController,
		};
	}

	private prepareRetryRequestBody(
		body: object | undefined,
		method: Method,
		requestOptions: RequestOptions,
	) {
		const clonedRequestOptions = { ...requestOptions };

		if ([Method.GET, Method.DELETE].includes(method)) {
			delete clonedRequestOptions.body; // Ensure body is removed for GET & DELETE
			return clonedRequestOptions;
		}

		if (body instanceof FormData) {
			delete clonedRequestOptions.headers?.['Content-Type'];
			clonedRequestOptions.body = body;
		}

		try {
			let jsonBody = JSON.parse(body as any);
			clonedRequestOptions.headers['Content-Type'] = 'application/json';
			clonedRequestOptions.body = JSON.stringify(jsonBody);
		} catch (error) {
			clonedRequestOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded';
			clonedRequestOptions.body = body as any;
		}

		return clonedRequestOptions;
	}

	private prepareTimeoutResponse(): Response {
		return new Response('Request timed out', {
			status: 499,
			statusText: 'Request timed out',
		});
	}

	/**
	 * Merges default request options with custom options if it's necessary.
	 * @param customOptions - RequestOptions
	 * @returns RequestOptions
	 */
	private prepareRequestOptions(customOptions?: RequestOptions) {
		if (!customOptions) {
			return this.defaultOptions;
		}

		const { headers, ...others } = customOptions;

		if (headers) {
			return {
				...this.defaultOptions,
				...others,
				headers: { ...this.defaultOptions.headers, ...headers },
			};
		}

		return { ...this.defaultOptions, ...customOptions };
	}
}
